import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ErrorNotice } from '../common/ErrorNotice';
import { useApiErrorTranslation } from '@/hooks/useApiErrorTranslation';
import { API_ENDPOINTS, useApiRequest } from '@/hooks/useApiRequest';
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat';

type Reservation = {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  status: 'scheduled' | 'cancelled';
};

type ReservationHistoryContainerProps = {
  spaceType?: 'shower_room' | 'private_room';
  selectedServiceType: string;
  onNoReservations?: () => void;
};

export const ReservationHistoryContainer = ({
  spaceType,
  selectedServiceType,
  onNoReservations,
}: ReservationHistoryContainerProps) => {
  const { t } = useTranslation();
  const { translateApiError } = useApiErrorTranslation();
  const { formatHistoryDate, formatHistoryTimeRangeJst } = useDateTimeFormat();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [hasNoReservations, setHasNoReservations] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isRequesting, execute } = useApiRequest(
    'GET',
    API_ENDPOINTS.GET.RESERVATION_HISTORY,
  );

  useEffect(() => {
    if (!spaceType) {
      return;
    }

    const fetchReservations = async () => {
      setHasNoReservations(false);
      setError(null);
      const result = await execute({ space_type: spaceType });

      if (!result.ok) {
        const { message } = translateApiError(result.error);
        setError(message);
        return;
      }

      if (result.data.length === 0) {
        setHasNoReservations(true);
        onNoReservations?.();
        return;
      }

      setReservations(result.data);
    };

    fetchReservations();
  }, [execute, spaceType, onNoReservations, translateApiError]);

  // spaceTypeが選択されていない場合は何も表示しない
  if (!spaceType) {
    return null;
  }

  if (isRequesting) {
    return (
      <div className="reservation-card__history-title">
        {t('common.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <>
        <ErrorNotice message={error} />
      </>
    );
  }

  // 予約がない場合は何も表示しない（ダイアログは親コンポーネントで表示）
  if (hasNoReservations) {
    return null;
  }

  const serviceTypeClass =
    selectedServiceType === 'shower' ? 'shower' : 'booth';

  return (
    <div className="reservation-card__history-lists">
      <div
        className={`reservation-card__history-list reservation-card__history-list--${serviceTypeClass}`}
        data-history-id={serviceTypeClass}
      >
        {reservations.map((reservation) => {
          const isInactive = reservation.status === 'cancelled';

          return (
            <div
              key={reservation.id}
              className="reservation-card__history-item"
            >
              {isInactive ? (
                <div className="reservation-card__history-link reservation-card__history-link--inactive">
                  <div className="reservation-card__history-row">
                    <div className="reservation-card__history-status">
                      {t('reservation.history.cancelledLabel')}
                    </div>
                    <div className="reservation-card__history-date">
                      {formatHistoryDate(reservation.date)}
                    </div>
                    <div className="reservation-card__history-time">
                      {formatHistoryTimeRangeJst(
                        reservation.start_time,
                        reservation.end_time,
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  to={`/reservation/history/${reservation.id}`}
                  className="reservation-card__history-link"
                >
                  <div className="reservation-card__history-row">
                    <div className="reservation-card__history-date">
                      {formatHistoryDate(reservation.date)}
                    </div>
                    <div className="reservation-card__history-time">
                      {formatHistoryTimeRangeJst(
                        reservation.start_time,
                        reservation.end_time,
                      )}
                    </div>
                  </div>
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
